import { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  LinearProgress,
  TextField,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { ClientTypes, UserTypes } from "../../features/auth/types/authType";
import { Company } from "../../features/setups/types/setupTypes";
import { selectAuth } from "../../features/auth/authSlice";
import { selectSetups } from "../../features/setups/setupSlices";
import { fetchCompanies } from "../../features/setups/setupSlices";
import { useTranslation } from "react-i18next";

const ALL_COMPANY_OPTION: Company = {
  _id: "-1",
  displayName: "All",
  email: "all@ultramanpowercompany.com",
};

interface Props {
  setCompany: any;
  companyType?: ClientTypes;
  /** When set, includes partners matching any of these types (overrides companyType). */
  companyTypes?: ClientTypes[];
  companyId?: string | undefined;
  includeAll?: boolean | undefined;
  includeModerator?: boolean | undefined;
  /** When set, keeps the autocomplete aligned with the parent selection. */
  value?: Company;
  /** Pre-loaded foreign offices (Calling module); skips user.company.partners. */
  offices?: Company[];
}
export const CompanyFilter = ({
  setCompany,
  companyType,
  companyTypes,
  companyId,
  includeAll,
  includeModerator = false,
  value,
  offices,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [userSelectedCompany, setSelectedCompany] = useState<Company>();
  const { user, loading: loadingUser } = useAppSelector(selectAuth);
  const {
    companiesWithCount: { companies: allCompanies },
    loading: companiesLoading,
  } = useAppSelector(selectSetups);

  const isSuperUser = user?.userType === UserTypes.Super;
  const initializedRef = useRef(false);
  const companyTypesKey = companyTypes?.join(",") ?? "";

  useEffect(() => {
    if (isSuperUser && allCompanies.length === 0) {
      dispatch(fetchCompanies({ skip: 0, take: 500 }));
    }
  }, [dispatch, isSuperUser, allCompanies.length]);

  const companies = useMemo(() => {
    if (offices?.length) {
      return includeAll ? [ALL_COMPANY_OPTION, ...offices] : offices;
    }

    if (!user || loadingUser === "pending") return [];

    if (isSuperUser) {
      if (allCompanies.length === 0) return [];
      return [ALL_COMPANY_OPTION, ...allCompanies];
    }

    const partners = user.company?.partners;
    if (!partners?.length) return [];

    const resolvedCompanyTypes = companyTypesKey
      ? (companyTypesKey.split(",") as ClientTypes[])
      : undefined;

    const byType = resolvedCompanyTypes?.length
      ? partners.filter(
          (c) => c.type != null && resolvedCompanyTypes.includes(c.type),
        )
      : companyType
        ? partners.filter((c) => c.type === companyType)
        : [...partners];

    if (includeAll) return [ALL_COMPANY_OPTION, ...byType];
    if (includeModerator && user.company) {
      return [
        {
          _id: user.company._id,
          displayName: user.company.displayName,
          email: user.company.email,
        },
        ...byType,
      ];
    }
    return byType;
  }, [
    offices,
    user,
    loadingUser,
    isSuperUser,
    allCompanies,
    companyType,
    companyTypesKey,
    includeAll,
    includeModerator,
  ]);

  // useEffect(() => {
  //   if (user) {
  //     if (user.company && user.company.partners) {
  //       if (user.company.type === ClientTypes.Moderator) {
  //         if (includeAll)
  //           setCompanies([
  //             { _id: "-1", displayName: "All", email: "all@ultramanpowercompany.com" },
  //             ...user.company.partners,
  //           ]);
  //         else setCompanies(user.company.partners);
  //       } else {
  //         dispatch(getCompany(user.company.partners[0]._id as string));
  //       }
  //     }
  //   }
  // }, [dispatch, user]);

  // useEffect(() => {
  //   if (
  //     parentCompany &&
  //     parentCompany.partners &&
  //     parentCompany.partners.length > 0 &&
  //     parentCompany.type === ClientTypes.Moderator &&
  //     user &&
  //     user.company
  //   ) {
  //     //console.log(parentCompany.partners);
  //     if (user.company.type === ClientTypes.LocalAgency) {
  //       if (includeAll)
  //         setCompanies([
  //           { _id: "-1", displayName: "All", email: "all@cvplus.com" },
  //           ...parentCompany.partners.filter(
  //             (c) => c.type === ClientTypes.ForeignAgent
  //           ),
  //         ]);
  //       else
  //         setCompanies([
  //           ...parentCompany.partners.filter(
  //             (c) => c.type === ClientTypes.ForeignAgent
  //           ),
  //         ]);
  //     } else if (user.company.type === ClientTypes.ForeignAgent) {
  //       if (includeAll)
  //         setCompanies([
  //           { _id: "-1", displayName: "All", email: "all@cvplus.com" },
  //           ...parentCompany.partners.filter(
  //             (c) => c.type === ClientTypes.LocalAgency
  //           ),
  //         ]);
  //       else
  //         setCompanies([
  //           ...parentCompany.partners.filter(
  //             (c) => c.type === ClientTypes.LocalAgency
  //           ),
  //         ]);
  //     }
  //   }
  // }, [dispatch, parentCompany]);

  // useEffect(() => {
  //   if (user) {
  // if (user.userType === UserTypes.Super) {
  //   dispatch(
  //     fetchCompanies({ type: ClientTypes.ForeignAgent, skip: 0, take: 500 })
  //   );
  // } else if (user.company && user.company.partners) {
  //   if (includeAgency)
  //     setCompanies([...user.company.partners, user.company]);
  //   else if (includeAll)
  //     setCompanies([
  //       { _id: "-1", displayName: "All", email: "all@ultramanpowercompany.com" },
  //       ...user.company.partners,
  //     ]);
  //   else setCompanies(user.company.partners);
  // }

  //   }
  // }, [dispatch, parentCompany]);

  const isLoading =
    !offices?.length &&
    (loadingUser === "pending" ||
      (isSuperUser && companiesLoading === "pending") ||
      companies.length < 1);

  useEffect(() => {
    if (companies.length === 0) return;

    if (value != null) {
      const matched =
        companies.find((c) => String(c._id) === String(value._id)) ?? value;
      setSelectedCompany(matched);
      initializedRef.current = true;
      return;
    }

    if (companyId && companyId.length > 0) {
      const matched = companies.find((c) => String(c._id) === String(companyId));
      if (matched) {
        setSelectedCompany(matched);
        setCompany(matched);
        initializedRef.current = true;
      }
      return;
    }

    if (!initializedRef.current) {
      initializedRef.current = true;
      const defaultCompany = companies[0];
      setCompany(defaultCompany);
      setSelectedCompany(defaultCompany);
    }
  }, [companies, companyId, setCompany, value]);

  return (
    <Box
      sx={
        {
          //backgroundColor: "white",
          //color: "secondary.main",
          //borderRadius: "5px",
          // p: 0.5,
          // pb: 1,
          // mt: 0.5,
        }
      }
    >
      {isLoading ? (
        <LinearProgress color="secondary" />
      ) : (
        <Autocomplete
          id="companyId"
          options={companies}
          value={userSelectedCompany ?? null}
          isOptionEqualToValue={(option, val) =>
            String(option._id) === String(val._id)
          }
          getOptionLabel={(option) =>
            option.displayName.substring(
              0,
              option.displayName.indexOf(" ") > 0
                ? option.displayName.indexOf(" ")
                : 10,
            )
          }
          sx={{
            mt: 0,
            minWidth: "200px",
            color: "secondary.main",
            p: 0.5,
            borderRadius: "5px",
            ".MuiSvgIcon-root": {
              color: "white",
            },
          }}
          loading={isSuperUser && companiesLoading === "pending"}
          onChange={(e, value) => {
            setSelectedCompany(value as Company);
            setCompany(value as Company);
          }}
          size="small"
          renderInput={(params) => (
            <TextField
              label={
                companyType
                  ? companyType === ClientTypes.ForeignAgent
                    ? t("pro")
                    : t("epro")
                  : t("company")
              }
              name="companyId"
              {...params}
              variant="outlined"
            />
          )}
        />
      )}
    </Box>
  );
};
